export const ORG_ROLE_OPTIONS = [
  {
    slug: "admin",
    label: "Admin",
    description: "Full access to members, projects, API keys, billing, and org settings.",
  },
  {
    slug: "billing-admin",
    label: "Billing",
    description: "Manage billing and members, and view all projects and usage. Cannot manage API keys.",
  },
  {
    slug: "project-owner",
    label: "Project Owner",
    description: "Create projects and manage members and API keys across the org. Cannot manage billing.",
  },
  {
    slug: "developer",
    label: "Developer",
    description: "Create and manage API keys on assigned projects only.",
  },
  {
    slug: "readonly",
    label: "View Only",
    description: "View assigned projects and org-wide usage. Cannot change settings or API keys.",
  },
] as const;

export type OrgRoleSlug = (typeof ORG_ROLE_OPTIONS)[number]["slug"];

/** Roles users may assign in invite/edit pickers. */
export const ORG_ROLE_ASSIGNABLE_OPTIONS = ORG_ROLE_OPTIONS;

export const orgRoleCanManageMembers = (slug: string): boolean =>
  slug === "admin" || slug === "billing-admin" || slug === "project-owner";

export const orgRoleNeedsProjectGrants = (slug: string): boolean =>
  slug === "developer" || slug === "readonly" || slug === "member";

/** Role-level match for OrgAccess::canMutateProject (developer still needs a grant). */
export const orgRoleCanMutateProjects = (slug: string | null | undefined): boolean =>
  slug === "admin" || slug === "project-owner" || slug === "developer";

export const orgRoleCanMutateKeys = (slug: string | null | undefined): boolean => orgRoleCanMutateProjects(slug);

export const orgRoleCanCreateProjects = (slug: string | null | undefined): boolean =>
  slug === "admin" || slug === "project-owner";

export const ASK_ADMIN_TO_CREATE_PROJECT_HINT = "Ask an admin or project owner to create a project first.";

export const orgRoleCanUseChat = (slug: string | null | undefined): boolean =>
  slug === "admin" || slug === "project-owner" || slug === "developer";

export const orgRoleCanManageBilling = (slug: string | null | undefined): boolean =>
  slug === "admin" || slug === "billing-admin";

export const orgRoleCanManageOrgSettings = (slug: string | null | undefined): boolean => slug === "admin";

export const orgRoleLabel = (slug: string): string => {
  if (slug === "member") {
    return "Developer";
  }
  return ORG_ROLE_OPTIONS.find((role) => role.slug === slug)?.label ?? slug;
};

/** 16 swatches matching the Create/Edit project Figma color grid. */
export const PROJECT_COLOR_SWATCHES = [
  "#B8E0C8",
  "#F5E6A3",
  "#F5C99B",
  "#F5A89A",
  "#9DD9D2",
  "#9BC9F5",
  "#C5B4F0",
  "#F0B4D0",
  "#3D8B5F",
  "#C4A035",
  "#C45C26",
  "#C62828",
  "#2A8A80",
  "#2F5FBF",
  "#5B2D9E",
  "#B01A6B",
] as const;
